import { AppleIcon } from "@/components/Icons/AppleIcon";
import { GoogleIcon } from "@/components/Icons/GoogleIcon";
import { SignupErrorFeedback } from "../SignupErrorFeedback/SignupErrorFeedback";
import { ucfirst } from "@/utils/string";
import { IoIcon } from "@/components/Icons/IoIcon";
import { CarouselSlide } from "@/types/carousel";
import { useAuthActions, useAuthState } from "@/store/auth";
import { useLocation, useRoute } from "wouter";
import { useEffect, useState } from "react";
import { explorerPrefixedPath } from "@/utils";
import { cn } from "@/utils/styles";
import { Loader } from "@/components/Common/Loader/Loader";
import { TermsAndConditionsModal } from "@/components/Common/TermsAndConditionsModal/TermsAndConditionsModal";
import env from "@/env";
import { WorldCoin } from "@/components/Icons/WorldCoinIcon";
import { TwitterIcon } from "@/components/Icons/TwitterIcon";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { PasswordLessLogin } from "./PasswordLessLogin";
import { Separator } from "@/components/ui/separator";
import { GithubIcon } from "@/components/Icons/GithubIcon";
import { HuggingFaceInvertedIcon } from "@/components/Icons/HuggingFaceInvertedIcon";
import { EmailIcon } from "@/components/Icons/EmailIcon";

export const LoginStep: React.FC<CarouselSlide> = () => {
  const [, pageParams] = useRoute(explorerPrefixedPath("/login/:section"));
  const isSignup = pageParams?.section === "signup";

  const { beginSso } = useAuthActions();
  const failedError = useAuthState((state) => state.error);
  const ssoType = useAuthState((state) => state.ssoType);
  const [, setLocation] = useLocation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isTermsAndConditionsOpen, setIsTermsAndConditionsOpen] = useState(false);
  const [, setIsOTPOpen] = useState(false);

  useEffect(() => {
    setSubmitting(false);
  }, [failedError]);

  useEffect(() => {
    const pageVisibilityHandler = () => {
      setSubmitting(false);
    };

    window.addEventListener("pagehide", pageVisibilityHandler, false);
    return () => {
      window.removeEventListener("pagehide", pageVisibilityHandler);
    };
  }, []);

  return (
    <>
      <TermsAndConditionsModal
        isOpen={isTermsAndConditionsOpen}
        onClose={() => {
          setIsTermsAndConditionsOpen(false);
        }}
      />
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex w-[300px] flex-col gap-5 py-6 text-center">
          <div className="flex flex-row justify-center">
            <div className="flex size-[64px] flex-row items-center justify-center rounded-lg border border-gray-dark-200 bg-gray-dark-600">
              <IoIcon className="h-[16px] text-foreground" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="title-h4 text-foreground">
              One ID
              <br />
              Unlimited Access
            </h2>
            <p className="subtext-x-small text-gray-dark-1000">One account for everything IO</p>
          </div>

          {failedError && <SignupErrorFeedback>{ucfirst(failedError)}</SignupErrorFeedback>}

          <PasswordLessLogin
            onCancel={() => {
              setIsOTPOpen(false);
            }}
          />

          <Separator className="flex flex-row items-center justify-center">
            <div className="flex flex-row items-center justify-center bg-black px-2">OR</div>
          </Separator>

          <>
            <div className="flex flex-col gap-2">
              {LOGIN_OPTIONS.map((option) => {
                const { value, label } = option;

                if (["otp"].includes(value)) {
                  return;
                }

                return (
                  <a
                    key={value}
                    className={cn(
                      `subtext-medium flex flex-row items-center justify-center gap-3 rounded border border-gray-dark-1100 px-4 py-3 text-foreground transition-all hover:border-white hover:bg-white hover:text-black`,
                      isSubmitting ? `pointer-events-none` : `cursor-pointer`
                    )}
                    onClick={async () => {
                      if (value === "otp") {
                        setIsOTPOpen(true);
                        return;
                      }

                      trackEvent(
                        isSignup
                          ? AnalyticsEventType.SIGN_UP_INITIATED
                          : AnalyticsEventType.SIGNED_IN,
                        {
                          type: value
                        }
                      );
                      window.dataLayer?.push({
                        event: isSignup
                          ? AnalyticsEventType.SIGN_UP_INITIATED
                          : AnalyticsEventType.SIGNED_IN,
                        type: value
                      });
                      setSubmitting(true);

                      try {
                        await beginSso(value);
                      } catch {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {option.icon && <option.icon className="h-[24px]" />}
                    {`Sign ${isSignup ? "up" : "in"} with ${label}`}
                    {isSubmitting && value === ssoType && (
                      <Loader className="!h-[14px] !w-[14px]" color="#fff" />
                    )}
                  </a>
                );
              })}
            </div>

            {isSignup ? (
              <p className="subtext-small text-gray-dark-1000">
                Already a Member?{" "}
                <a
                  onClick={() => setLocation(explorerPrefixedPath("/login"))}
                  className="cursor-pointer text-foreground hover:underline"
                >
                  Sign In Here
                </a>
              </p>
            ) : (
              <p className="subtext-small text-gray-dark-1000">
                Don't have an account?{" "}
                <a
                  onClick={() => {
                    trackEvent(AnalyticsEventType.SIGN_UP_CLICKED);
                    setLocation(explorerPrefixedPath("/login/signup"));
                  }}
                  className="cursor-pointer text-foreground hover:underline"
                >
                  Sign Up Here
                </a>
              </p>
            )}
          </>
        </div>
      </div>
    </>
  );
};

const EmailWrappedIcon = () => {
  return <EmailIcon className="h-[20px] text-gray-dark-2200 group-hover:text-black" />;
};

export const LOGIN_OPTIONS = [
  {
    value: "google-oauth2",
    label: "Google",
    icon: GoogleIcon
  },
  {
    value: "apple",
    label: "Apple",
    icon: AppleIcon
  },
  {
    value: "github",
    label: "Github",
    icon: GithubIcon
  },
  {
    value: "hugging-face",
    label: "Huggingface",
    icon: HuggingFaceInvertedIcon,
    deprecated: true
  },
  {
    value: "twitter",
    label: "Twitter",
    icon: TwitterIcon,
    deprecated: true
  },
  {
    value: "worldcoin",
    label: "Worldcoin",
    icon: WorldCoin,
    deprecated: true
  },
  {
    value: "otp",
    label: "Email",
    icon: EmailWrappedIcon
  }
].filter(({ value }) => {
  if (!env.LOGIN_OPTIONS || value === "other") {
    return true;
  }
  return env.LOGIN_OPTIONS.indexOf(value) > -1;
});
